import { useState, useEffect } from 'react'
import { fetchWithAuth } from '../utils/api'
import { normalizeToLatLng } from '../utils/geo'
import { assetIcon, taskIcon, wildlifeIcon } from '../utils/icons'

interface ActivityItem {
  id: string
  type: 'task' | 'wildlife' | 'asset' | 'map_link'
  title: string
  description: string
  timestamp: string
  icon: string
  user: string
  site?: string
  subcategory?: string
  position?: [number, number] // [lat, lng]
  direction_deg?: number
  speed_mps?: number
}

interface ActivityFeedProps {
  maxItems?: number
  onLocate?: (position: [number, number]) => void
  wildlifeOnly?: boolean
  onWildlifeOnlyChange?: (value: boolean) => void
}

function ActivityFeed({ maxItems = 5, onLocate, wildlifeOnly = false, onWildlifeOnlyChange }: ActivityFeedProps) {
  const [activities, setActivities] = useState<ActivityItem[]>([])
  const [loading, setLoading] = useState(true)
  const [wildlifeTypes, setWildlifeTypes] = useState<Record<string, { name: string; icon: string; color: string }>>({})
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null)
  const [expanded, setExpanded] = useState(false)
  const [dense, setDense] = useState(false)

  useEffect(() => {
    fetchWildlifeTypes().finally(fetchRecentActivity)
    // Try to get user location (optional)
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setUserLocation([pos.coords.latitude, pos.coords.longitude]),
        () => {},
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
      )
    }
  }, [])

  const fetchWildlifeTypes = async () => {
    try {
      const res = await fetchWithAuth('/api/assets/wildlife-types')
      if (res.ok) {
        const data = await res.json()
        setWildlifeTypes(data)
      }
    } catch (e) {
      // ignore
    }
  }

  const fetchRecentActivity = async () => {
    try {
      setLoading(true)
      const activityItems: ActivityItem[] = []

      // Fetch recent tasks
      try {
        const tasksResponse = await fetchWithAuth('/api/tasks')
        if (tasksResponse.ok) {
          const tasksData = await tasksResponse.json()
          const recentTasks = (tasksData.tasks || [])
            .map((task: any) => {
              // Attempt to normalize task position to [lat,lng] if available
              let position: [number, number] | undefined
              position = normalizeToLatLng(task.location_coords as [number, number])
              return ({
                id: `task-${task.id}`,
                type: 'task' as const,
                title: task.title,
                description: `${task.status} - ${task.site_name}`,
                timestamp: task.created_at,
                icon: taskIcon(),
                user: task.created_by_name,
                site: task.site_name,
                position
              })
            })
          activityItems.push(...recentTasks)
        }
      } catch (error) {
        console.error('Failed to fetch tasks:', error)
      }

      // Fetch recent wildlife observations
      try {
        const wildlifeResponse = await fetchWithAuth('/api/assets?type=wild_animal')
        if (wildlifeResponse.ok) {
          const wildlifeData = await wildlifeResponse.json()
          const recentWildlife = (wildlifeData.assets || [])
            .map((asset: any) => ({
              id: `wildlife-${asset.id}`,
              type: 'wildlife' as const,
              title: asset.name,
              description: '',
              timestamp: asset.time_started,
              icon: wildlifeIcon(asset.subcategory, wildlifeTypes),
              user: asset.created_by_name,
              site: asset.site_name,
              subcategory: asset.subcategory,
              position: normalizeToLatLng(asset.position_coords as [number, number]),
              direction_deg: typeof asset.direction_deg === 'number' ? asset.direction_deg : undefined,
              speed_mps: typeof asset.speed_mps === 'number' ? asset.speed_mps : undefined,
            }))
          activityItems.push(...recentWildlife)
        }
      } catch (error) {
        console.error('Failed to fetch wildlife:', error)
      }

      // Fetch recent assets (non-wildlife)
      try {
        const assetsResponse = await fetchWithAuth('/api/assets')
        if (assetsResponse.ok) {
          const assetsData = await assetsResponse.json()
          const recentAssets = (assetsData.assets || [])
            .filter((asset: any) => asset.type !== 'wild_animal')
            .map((asset: any) => ({
              id: `asset-${asset.id}`,
              type: 'asset' as const,
              title: asset.name,
              description: `${asset.type} - ${asset.site_name}`,
              timestamp: asset.created_at,
              icon: assetIcon(asset.type, asset.subcategory),
              user: asset.created_by_name,
              site: asset.site_name,
              position: normalizeToLatLng(asset.position_coords as [number, number])
            }))
          activityItems.push(...recentAssets)
        }
      } catch (error) {
        console.error('Failed to fetch assets:', error)
      }

      // Sort by timestamp and limit
      const sortedActivities = activityItems
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())

      setActivities(sortedActivities)
    } catch (error) {
      console.error('Failed to fetch activity:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatTimeAgo = (timestamp: string): string => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMinutes / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMinutes < 1) return 'Just now'
    if (diffMinutes < 60) return `${diffMinutes}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toISOString().slice(0, 10)
  }

  const distanceKm = (from: [number, number], to: [number, number]) => {
    const R = 6371 // km
    const toRad = (d: number) => d * Math.PI / 180
    const dLat = toRad(to[0] - from[0])
    const dLng = toRad(to[1] - from[1])
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) + Math.cos(toRad(from[0])) * Math.cos(toRad(to[0])) * Math.sin(dLng/2) * Math.sin(dLng/2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }

  const renderDistance = (item: ActivityItem) => {
    if (!userLocation || !item.position) return null
    try {
      const d = distanceKm(userLocation, item.position)
      const label = d < 0.5 ? `${Math.round(d * 1000)} m` : `${d.toFixed(1)} km`
      return <span className="activity-item__distance"> • {label} away</span>
    } catch { return null }
  }

  if (loading) {
    return (
      <div className="activity-feed">
        <div className="activity-feed__top">
          <h3 className="activity-feed__title">Recent Activity</h3>
        </div>
        <div className="activity-feed__loading">
          <div className="loading-spinner">Loading activity...</div>
        </div>
      </div>
    )
  }

  if (activities.length === 0) {
    return (
      <div className="activity-feed">
        <div className="activity-feed__top">
          <h3 className="activity-feed__title">Recent Activity</h3>
        </div>
        <div className="activity-feed__empty">
          <p>No recent activity to show</p>
          <p>Start by creating a task or recording wildlife!</p>
        </div>
      </div>
    )
  }

  const filtered = wildlifeOnly ? activities.filter(a => a.type === 'wildlife') : activities
  const visibleActivities = expanded ? filtered : filtered.slice(0, maxItems)
  const showToggle = activities.length > maxItems

  return (
    <div className={`activity-feed ${dense ? 'activity-feed--dense' : ''}`}>
      <div className="activity-feed__top">
        <h3 className="activity-feed__title">Recent Activity</h3>
        {showToggle && (
          <button
            className="btn activity-feed__toggle"
            onClick={() => setExpanded(e => !e)}
            aria-expanded={expanded}
          >
            {expanded ? 'Show less' : 'Show more'}
          </button>
        )}
        <label style={{ marginLeft: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.9rem' }}>
          <input type="checkbox" checked={wildlifeOnly} onChange={(e) => onWildlifeOnlyChange && onWildlifeOnlyChange(e.target.checked)} />
          Wildlife only
        </label>
        <label style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.9rem' }}>
          <input type="checkbox" checked={dense} onChange={(e) => setDense(e.target.checked)} />
          Dense
        </label>
      </div>
      <div className={`activity-feed__list ${expanded ? 'activity-feed__list--scroll' : ''}`}>
        {visibleActivities.map((activity) => (
          <div
            key={activity.id}
            className={`activity-item ${activity.position ? 'activity-item--clickable' : ''}`}
            onClick={() => {
              if (activity.position && typeof onLocate === 'function') onLocate(activity.position)
            }}
            role={activity.position ? 'button' : undefined}
            aria-label={activity.position ? `Locate ${activity.title} on map` : undefined}
          >
            <div className="activity-item__icon">
              {activity.type === 'wildlife'
                ? (wildlifeTypes[activity.subcategory || '']?.icon || activity.icon)
                : activity.icon}
            </div>
            <div className="activity-item__content">
              <div className="activity-item__header">
                <h4 className="activity-item__title">
                  {activity.type === 'wildlife' ? (
                    <>
                      <strong>{activity.title}</strong>
                      <span className="activity-item__suffix"> observed</span>
                    </>
                  ) : (
                    activity.title
                  )}
                </h4>
              </div>
              <div className="activity-item__sub">
                <span className="activity-item__time">{formatTimeAgo(activity.timestamp)}</span>
                {renderDistance(activity)}
                {!dense && activity.type === 'wildlife' && typeof activity.direction_deg === 'number' && (
                  <span className="activity-item__direction"> • 🧭 {Math.round(((activity.direction_deg % 360) + 360) % 360)}°</span>
                )}
              </div>
              {activity.description ? (
                <p className="activity-item__description">{activity.description}</p>
              ) : null}
              {!dense && (
                <div className="activity-item__meta">
                  <span className="activity-item__user">👤 {activity.user}</span>
                  {activity.site && (
                    <span className="activity-item__site">🏠 {activity.site}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default ActivityFeed
